// FocusContext, the "shared playhead" (workspace-modes note §4, §11.4): one
// `{company, scope, period, subject, thread?}` held by the shell and synced to the URL query
// (`s`, `p`, `scope`), so switching mode re-renders the same subject and week through a new
// lens and a deep link restores it. A switch never clears anything.
import { useNavigate, useParams, useRouterState, useSearch } from "@tanstack/react-router";
import { createContext, type ReactNode, useCallback, useContext, useMemo } from "react";
import type { CompanyAccess } from "../features/company";

export type FocusSearch = { s?: string; p?: string; scope?: string; thread?: string };

export const SUBJECT_KINDS = ["seat", "person", "task", "de", "team"] as const;
export type SubjectKind = (typeof SUBJECT_KINDS)[number];
export type Subject = { kind: SubjectKind; id: string };

const PERIOD = /^\d{4}-W(0[1-9]|[1-4]\d|5[0-3])$/;
const SCOPE = /^(company|me|(team|seat|department):[A-Za-z0-9_-]{1,64})$/;
const ID = /^[A-Za-z0-9_-]{1,64}$/;

export function parseSubject(value: unknown): Subject | null {
  if (typeof value !== "string") return null;
  const [kind, id, extra] = value.split(":");
  if (extra !== undefined || !id || !ID.test(id)) return null;
  return (SUBJECT_KINDS as readonly string[]).includes(kind ?? "")
    ? { kind: kind as SubjectKind, id }
    : null;
}

export function formatSubject(subject: Subject): string {
  return `${subject.kind}:${subject.id}`;
}

/** Router `validateSearch`: keeps only well-formed focus keys, drops the rest silently. */
export function validateFocusSearch(search: Record<string, unknown>): FocusSearch {
  const out: FocusSearch = {};
  if (parseSubject(search.s)) out.s = search.s as string;
  if (typeof search.p === "string" && PERIOD.test(search.p)) out.p = search.p;
  if (typeof search.scope === "string" && SCOPE.test(search.scope)) out.scope = search.scope;
  if (typeof search.thread === "string" && ID.test(search.thread)) out.thread = search.thread;
  return out;
}

/** The focus keys of a search object, for links that change mode but keep the playhead. */
export function keepFocus(search: Record<string, unknown>): FocusSearch {
  return validateFocusSearch(search);
}

export type Focus = {
  company: { id: string; slug: string };
  scope: string;
  period: string | null;
  subject: Subject | null;
  thread: string | null;
};

export type FocusPatch = {
  scope?: string | null;
  period?: string | null;
  subject?: Subject | null;
  thread?: string | null;
};

type FocusValue = { focus: Focus; setFocus: (patch: FocusPatch) => void };

const FocusContext = createContext<FocusValue | null>(null);

export function FocusProvider({
  company,
  children,
}: {
  company: CompanyAccess;
  children: ReactNode;
}) {
  const search = validateFocusSearch(useSearch({ strict: false }) as Record<string, unknown>);
  const navigate = useNavigate();

  const focus = useMemo<Focus>(
    () => ({
      company: { id: company.id, slug: company.slug },
      scope: search.scope ?? "company",
      period: search.p ?? null,
      subject: parseSubject(search.s),
      thread: search.thread ?? null,
    }),
    [company.id, company.slug, search.scope, search.p, search.s, search.thread],
  );

  // Routes whose path carries part of the focus: the week of a report, the DE of a profile.
  const params = useParams({ strict: false }) as { week?: string; id?: string };
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  /**
   * Every focus change is a navigation (a history entry, so back/forward restore it). When
   * the path itself carries the focused value, the path changes too, so the path and the
   * query can never disagree and no effect has to fight the user.
   */
  const setFocus = useCallback(
    (patch: FocusPatch) => {
      const search = (prev: Record<string, unknown>) => applyPatch(prev, patch);
      // Staying on the same screen keeps its own keys (the open tab); leaving drops them.
      const sameScreen = (prev: Record<string, unknown>) => ({
        ...screenKeys(prev),
        ...applyPatch(prev, patch),
      });
      const slug = company.slug;
      if (params.week && patch.period) {
        void navigate({
          to: "/c/$slug/kpis/reports/$week",
          params: { slug, week: patch.period },
          search,
        } as never);
        return;
      }
      // A DE's page in KPIs (its scorecard) or Agents (its profile): the DE is in the path.
      const [, , , deMode, page, sub] = pathname.split("/");
      if (
        params.id &&
        patch.subject !== undefined &&
        (deMode === "kpis" || deMode === "agents") &&
        page === "de"
      ) {
        const subject = patch.subject;
        void navigate(
          (subject?.kind === "de"
            ? {
                to: `/c/$slug/${deMode}/de/$id`,
                params: { slug, id: subject.id },
                search: sameScreen,
              }
            : {
                to: deMode === "kpis" ? "/c/$slug/kpis/scorecard" : "/c/$slug/agents",
                params: { slug },
                search,
              }) as never,
        );
        return;
      }
      // A seat's page in KPIs › Chart (M1-08): the seat is in the path.
      if (
        params.id &&
        patch.subject !== undefined &&
        deMode === "kpis" &&
        page === "chart" &&
        sub === "seat"
      ) {
        const subject = patch.subject;
        void navigate(
          (subject?.kind === "seat"
            ? {
                to: "/c/$slug/kpis/chart/seat/$id",
                params: { slug, id: subject.id },
                search: sameScreen,
              }
            : { to: "/c/$slug/kpis/chart", params: { slug }, search }) as never,
        );
        return;
      }
      void navigate({ to: ".", search: sameScreen } as never);
    },
    [navigate, company.slug, params.week, params.id, pathname],
  );

  const value = useMemo(() => ({ focus, setFocus }), [focus, setFocus]);
  return <FocusContext.Provider value={value}>{children}</FocusContext.Provider>;
}

export function useFocus(): FocusValue {
  const value = useContext(FocusContext);
  if (!value) throw new Error("useFocus must be used inside a company");
  return value;
}

const FOCUS_KEYS = new Set(["s", "p", "scope", "thread"]);

/** The non-focus string keys of a search object: a screen's own state, such as `tab`. */
export function screenKeys(search: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(search ?? {}))
    if (!FOCUS_KEYS.has(k) && typeof v === "string") out[k] = v;
  return out;
}

/** A search object with the focus patch applied (null or "" removes a key). */
export function applyPatch(prev: Record<string, unknown>, patch: FocusPatch): FocusSearch {
  const next: Record<string, unknown> = { ...prev };
  const set = (key: string, value: string | null | undefined) => {
    if (value === undefined) return;
    if (value === null || value === "") delete next[key];
    else next[key] = value;
  };
  set("scope", patch.scope === "company" ? null : patch.scope);
  set("p", patch.period);
  set("s", patch.subject === undefined ? undefined : patch.subject && formatSubject(patch.subject));
  set("thread", patch.thread);
  return validateFocusSearch(next);
}

/**
 * For a route's `beforeLoad`: the href with the path-carried focus written into the query,
 * or null when they already agree. Used with `redirect({ href, replace: true })`.
 */
export function reconcileHref(
  location: { pathname: string; search: unknown; hash?: string },
  patch: FocusPatch,
): string | null {
  const raw = validateFocusSearch(location.search as Record<string, unknown>);
  const next = applyPatch(raw, patch);
  if (JSON.stringify(raw) === JSON.stringify(next)) return null;
  // A screen's own keys (e.g. the DE profile's `tab`) ride along unchanged.
  const qs = new URLSearchParams({
    ...screenKeys(location.search as Record<string, unknown>),
    ...(next as Record<string, string>),
  }).toString();
  // The hash rides along too (a report's `#send` opens its drawer with the page).
  const hash = location.hash ? `#${location.hash}` : "";
  return `${qs ? `${location.pathname}?${qs}` : location.pathname}${hash}`;
}

/** ISO week key (`2026-W42`) of a date, Monday-based (ISO 8601). */
export function isoWeekKey(date: Date): string {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = Date.UTC(d.getUTCFullYear(), 0, 1);
  const week = Math.ceil(((d.getTime() - yearStart) / 86_400_000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

/** The last `count` ISO weeks ending with the week of `today`, newest first. */
export function recentWeeks(today: Date, count = 8): string[] {
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() - 7 * i);
    out.push(isoWeekKey(d));
  }
  return out;
}
